/**
 * Simple notification sound utility for chat.
 * Uses Web Audio API to generate a subtle beep sound.
 */

let audioContext: AudioContext | null = null;

// Initialize AudioContext lazily (must be done after user interaction)
const getAudioContext = (): AudioContext | null => {
  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    } catch {
      console.warn('Web Audio API not supported');
      return null;
    }
  }
  return audioContext;
};

/**
 * Play a simple, minimal beep notification sound.
 * Uses a soft sine wave for a pleasant, non-intrusive alert.
 */
export const playNotificationSound = (): void => {
  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    // Resume context if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    // Connect oscillator -> gain -> output
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    // Soft sine wave at a pleasant frequency
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, ctx.currentTime); // A5 note

    // Quick fade in and out for a soft "beep"
    const now = ctx.currentTime;
    const duration = 0.1; // 100ms beep

    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01); // Quick fade in
    gainNode.gain.linearRampToValueAtTime(0, now + duration); // Fade out

    // Play the sound
    oscillator.start(now);
    oscillator.stop(now + duration);
  } catch (e) {
    console.warn('Failed to play notification sound:', e);
  }
};

/**
 * Check if notifications are enabled (user preference).
 * Could be extended to read from localStorage or user settings.
 */
export const areNotificationsEnabled = (): boolean => {
  // For now, always enabled. Could add user preference later.
  return true;
};
